import React, { useState, useEffect, useRef } from 'react';
import { Upload, X, ArrowLeft, Plus } from 'lucide-react';
import Button from '../../../components/Button';
import { useAudience } from '../../../context/AudienceContext';
import {
  MAX_UPLOAD_SIZE_MB,
  ALLOWED_IMAGE_MIME_TYPES,
  ALLOWED_IMAGE_EXTENSIONS_RE,
  ACCEPTED_IMAGE_INPUT,
} from './conceptImageFormats';

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

interface PackagingVariantComparisonProps {
  onSubmit: (simulationId: number) => void;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  onBack: () => void;
  onEditStep?: () => void;
  initialFormData?: Record<string, any>;
  onFormDataChange?: (data: Record<string, any>) => void;
  onSavePayload?: (payload: Record<string, any>) => void;
}

type ImageFile = {
  id: string;
  file: File;
  preview: string;
};

const API_URL = import.meta.env.VITE_API_URL || '';

const MIN_VARIANTS = 2;
const MAX_VARIANTS = 5;

const PackagingVariantComparisonForm: React.FC<PackagingVariantComparisonProps> = ({
  onSubmit,
  selectedSegmentIds,
  personaFilters,
  onBack,
  onEditStep,
  initialFormData = {},
  onFormDataChange = () => {},
  onSavePayload,
}) => {
  const { audienceData } = useAudience();
  const [simName, setSimName] = useState(initialFormData.simName || '');
  const [brand, setBrand] = useState(initialFormData.brand || '');
  const [goal, setGoal] = useState(initialFormData.goal || '');
  const [variantImages, setVariantImages] = useState<ImageFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const variantFileInputRef = useRef<HTMLInputElement>(null);

  const handleEdit = () => {
    if (onEditStep) onEditStep();
  };

  const imagesLoadedRef = useRef(false);
  useEffect(() => {
    if (imagesLoadedRef.current) return;
    const existingImages = initialFormData.variantImages;
    if (!existingImages || !Array.isArray(existingImages) || existingImages.length === 0) return;
    imagesLoadedRef.current = true;

    const loaded: ImageFile[] = existingImages.map((imageData: string, i: number) => {
      let previewUrl = imageData;
      if (imageData && !imageData.startsWith('data:') && !imageData.startsWith('http')) {
        previewUrl = `${API_URL}${imageData}`;
      }
      return {
        id: `existing-variant-${Date.now()}-${i}`,
        file: new File([], `existing-variant-${i}.jpg`, { type: 'image/jpeg' }),
        preview: previewUrl,
      };
    });
    setVariantImages(loaded);
  }, [initialFormData.variantImages]);

  useEffect(() => {
    onFormDataChange({ simName, brand, goal });
  }, [simName, brand, goal, onFormDataChange]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleEdit();
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(
      (file) =>
        (ALLOWED_IMAGE_MIME_TYPES.includes(file.type) || ALLOWED_IMAGE_EXTENSIONS_RE.test(file.name))
        && file.size <= MAX_UPLOAD_SIZE_MB * 1024 * 1024
    );

    const remaining = MAX_VARIANTS - variantImages.length;
    const filesToAdd = validFiles.slice(0, remaining);

    const newImages: ImageFile[] = filesToAdd.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
    }));

    setVariantImages((prev) => [...prev, ...newImages]);
    if (e.target) e.target.value = '';
  };

  const removeImage = (imageId: string) => {
    handleEdit();
    setVariantImages((prev) => {
      const toRemove = prev.find((img) => img.id === imageId);
      if (toRemove) URL.revokeObjectURL(toRemove.preview);
      return prev.filter((img) => img.id !== imageId);
    });
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const imageToBase64 = async (img: ImageFile): Promise<string> => {
    if (img.preview.startsWith('data:')) return img.preview;
    if (img.preview.startsWith('http') || img.preview.startsWith('/')) {
      try {
        const response = await fetch(img.preview, { credentials: 'include' });
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch {
        return img.preview;
      }
    }
    if (img.file.size > 0) return fileToBase64(img.file);
    return img.preview;
  };

  const saveMode = !!onSavePayload;

  const handleSubmit = async () => {
    if (!simName.trim() || !brand.trim() || !goal.trim() || variantImages.length < MIN_VARIANTS) {
      alert(`Please fill in all required fields and upload ${MIN_VARIANTS}-${MAX_VARIANTS} variant images`);
      return;
    }

    setIsLoading(true);
    try {
      const variantImagesBase64 = await Promise.all(
        variantImages.map((img) => imageToBase64(img))
      );

      const payload = {
        audience_id: audienceData.audienceId,
        segment_ids: selectedSegmentIds,
        persona_filters: personaFilters,
        name: simName,
        description: `Packaging Variant Comparison - ${brand}`,
        goal_of_simulation: goal,
        message_to_test: '',
        task: 'packaging-variant-comparison',
        data: {
          brand,
          goal,
          variant_images: variantImagesBase64,
        },
      };

      if (onSavePayload) {
        onSavePayload(payload);
        return;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/simulations`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Failed to create simulation');
      const data = await response.json();
      onSubmit(data.simulation_id);
    } catch (error) {
      console.error('Error creating simulation:', error);
      alert('Failed to create simulation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const canAddMore = variantImages.length < MAX_VARIANTS;

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <Button variant="ghost" size="sm" onClick={onBack} icon={<ArrowLeft className="w-4 h-4 mr-1" />}>
            Back to use case selection
          </Button>
          <span className="text-sm text-gray-500">{selectedSegmentIds.length} segments selected</span>
        </div>
      </div>

      <div className="mb-6">
        <label htmlFor="simName" className="block text-sm font-medium text-gray-700 mb-2">
          Simulation Name <span className="text-red-500">*</span>
        </label>
        <input
          id="simName"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={simName}
          onChange={(e) => { setSimName(e.target.value); handleEdit(); }}
          placeholder="Enter a name for your comparison"
        />
      </div>

      <div className="mb-6">
        <label htmlFor="brand" className="block text-sm font-medium text-gray-700 mb-2">
          Brand Name <span className="text-red-500">*</span>
        </label>
        <input
          id="brand"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={brand}
          onChange={(e) => { setBrand(e.target.value); handleEdit(); }}
          placeholder="Enter your brand name"
        />
      </div>

      <div className="mb-6">
        <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-2">
          Goal <span className="text-red-500">*</span>
        </label>
        <input
          id="goal"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={goal}
          onChange={(e) => { setGoal(e.target.value); handleEdit(); }}
          placeholder="What are you trying to learn from this comparison?"
        />
      </div>

      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Variant Images <span className="text-red-500">*</span>
            <span className="text-xs text-gray-500 ml-2">({MIN_VARIANTS}-{MAX_VARIANTS} front panel images)</span>
          </label>
          {canAddMore && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => variantFileInputRef.current?.click()}
              aria-label="Add variant images"
              title="Add variant images"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Variant
            </Button>
          )}
          <input
            type="file"
            ref={variantFileInputRef}
            onChange={handleImageUpload}
            accept={ACCEPTED_IMAGE_INPUT}
            multiple
            className="hidden"
            aria-label="Upload variant images"
            title="Upload variant images"
          />
        </div>

        {variantImages.length === 0 ? (
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
            onClick={() => variantFileInputRef.current?.click()}
          >
            <Upload className="w-8 h-8 mx-auto text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">Click to upload variant packaging images</p>
            <p className="text-xs text-gray-400">Upload {MIN_VARIANTS}-{MAX_VARIANTS} front panel images to compare</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {variantImages.map((image, idx) => (
              <div key={image.id} className="relative">
                <div className="absolute top-1 left-1 bg-black bg-opacity-60 rounded px-2 py-0.5 z-10">
                  <span className="text-white text-xs font-medium">Variant {idx + 1}</span>
                </div>
                <img
                  src={image.preview}
                  alt={`Variant ${idx + 1}`}
                  className="h-40 w-full object-contain bg-gray-50 rounded-md border border-gray-300"
                />
                <button
                  type="button"
                  aria-label={`Remove variant ${idx + 1}`}
                  title={`Remove variant ${idx + 1}`}
                  className="absolute top-1 right-1 bg-black bg-opacity-50 rounded-full p-1 text-white hover:bg-opacity-70"
                  onClick={() => removeImage(image.id)}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}

            {canAddMore && (
              <div
                className="h-40 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors"
                onClick={() => variantFileInputRef.current?.click()}
              >
                <Plus className="w-6 h-6 text-gray-400" />
                <span className="mt-1 text-xs text-gray-500">Add variant</span>
              </div>
            )}
          </div>
        )}

        {variantImages.length > 0 && variantImages.length < MIN_VARIANTS && (
          <p className="mt-2 text-xs text-amber-600">
            Upload at least {MIN_VARIANTS} variant images to compare
          </p>
        )}
      </div>

      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={
            !simName.trim() || !brand.trim() || !goal.trim() ||
            variantImages.length < MIN_VARIANTS || isLoading
          }
          withArrow
        >
          {isLoading ? 'Comparing Variants...' : saveMode ? 'Continue' : 'Compare Variants'}
        </Button>
      </div>
    </div>
  );
};

export default PackagingVariantComparisonForm;